import { useEffect } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import { useMapStore } from '../../../stores/mapStore';
import { getTileRoot } from '../../../lib/env';
import type { LayerSettings } from '../LayerSettingsPanel';
import {
  FORECAST_LAYER_MAX_ZOOM,
  FORECAST_TILE_MAX_ZOOM,
  FORECAST_TILE_MIN_ZOOM,
  FORECAST_WEATHER_LAYER_DEFS,
  forecastWeatherFillLayerId,
  forecastWeatherPeriodTilesUrl,
  forecastWeatherSourceId,
  type ForecastWeatherLayerDef,
} from '../../../lib/forecastWeather';

type ForecastWeatherSettings = Pick<
  LayerSettings,
  | 'forecastWeatherTemp'
  | 'forecastWeatherPrecip'
  | 'forecastWeatherWind'
  | 'forecastWeatherTempPeriods'
  | 'forecastWeatherPrecipPeriods'
  | 'forecastWeatherWindPeriods'
>;

function tilesBase(): string {
  return getTileRoot();
}

function periodsForDef(settings: ForecastWeatherSettings, def: ForecastWeatherLayerDef): string[] {
  return settings[def.periodSettingsKey];
}

function sourceMinZoom(map: MapboxMap, sourceId: string): number | undefined {
  const spec = map.getStyle()?.sources?.[sourceId];
  if (!spec || spec.type !== 'vector') return undefined;
  return typeof spec.minzoom === 'number' ? spec.minzoom : undefined;
}

function insertBeforeId(map: MapboxMap): string | undefined {
  if (map.getLayer('ghana-flood-exposure-fill')) return 'ghana-flood-exposure-fill';
  if (map.getLayer('ghana-water')) return 'ghana-water';
  return undefined;
}

function ensureVectorSource(
  map: MapboxMap,
  sourceId: string,
  tileLayer: string,
  period: string,
) {
  const tileRoot = tilesBase();
  if (!tileRoot) return;

  const expectedMinZoom = FORECAST_TILE_MIN_ZOOM;
  const existingMinZoom = sourceMinZoom(map, sourceId);
  if (existingMinZoom !== undefined && existingMinZoom !== expectedMinZoom) {
    for (const layer of map.getStyle()?.layers ?? []) {
      if (layer.source === sourceId && map.getLayer(layer.id)) {
        map.removeLayer(layer.id);
      }
    }
    if (map.getSource(sourceId)) map.removeSource(sourceId);
  }

  if (!map.getSource(sourceId)) {
    map.addSource(sourceId, {
      type: 'vector',
      tiles: [forecastWeatherPeriodTilesUrl(tileRoot, tileLayer, period)],
      minzoom: expectedMinZoom,
      maxzoom: FORECAST_TILE_MAX_ZOOM,
    });
  }
}

function ensurePeriodFillLayer(map: MapboxMap, def: ForecastWeatherLayerDef, period: string) {
  const sourceId = forecastWeatherSourceId(def, period);
  const fillLayerId = forecastWeatherFillLayerId(def, period);
  ensureVectorSource(map, sourceId, def.tileLayer, period);

  if (!map.getLayer(fillLayerId)) {
    map.addLayer(
      {
        id: fillLayerId,
        type: 'fill',
        source: sourceId,
        'source-layer': def.sourceLayer,
        minzoom: FORECAST_TILE_MIN_ZOOM,
        maxzoom: FORECAST_LAYER_MAX_ZOOM,
        layout: { visibility: 'visible' },
        paint: def.paint,
      },
      insertBeforeId(map),
    );
  }

  map.setLayerZoomRange(fillLayerId, FORECAST_TILE_MIN_ZOOM, FORECAST_LAYER_MAX_ZOOM);
  map.setLayoutProperty(fillLayerId, 'visibility', 'visible');
}

function removePeriodFillLayer(map: MapboxMap, def: ForecastWeatherLayerDef, period: string) {
  const sourceId = forecastWeatherSourceId(def, period);
  const fillLayerId = forecastWeatherFillLayerId(def, period);
  if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId);
  if (map.getSource(sourceId)) map.removeSource(sourceId);
}

function syncVariableLayers(
  map: MapboxMap,
  def: ForecastWeatherLayerDef,
  enabled: boolean,
  selectedPeriods: string[],
) {
  const active = enabled ? new Set(selectedPeriods) : new Set<string>();
  const style = map.getStyle();

  if (style?.layers) {
    for (const layer of style.layers) {
      const id = layer.id;
      if (!id.startsWith(def.fillLayerIdPrefix)) continue;
      const period = id.slice(def.fillLayerIdPrefix.length);
      if (!active.has(period)) removePeriodFillLayer(map, def, period);
    }
  }

  if (!enabled) return;

  for (const period of selectedPeriods) {
    ensurePeriodFillLayer(map, def, period);
  }
}

function syncForecastWeatherLayers(map: MapboxMap, settings: ForecastWeatherSettings) {
  for (const def of FORECAST_WEATHER_LAYER_DEFS) {
    syncVariableLayers(map, def, settings[def.settingsKey], periodsForDef(settings, def));
  }
}

/** Forecast weather — per-day fill layers from tiled forecast data. */
export function useForecastWeatherLayers(
  mapRef: React.MutableRefObject<MapboxMap | null>,
  settings: ForecastWeatherSettings,
) {
  const { isMapLoaded } = useMapStore();
  const syncKey = FORECAST_WEATHER_LAYER_DEFS.map((def) => {
    const enabled = settings[def.settingsKey];
    const periods = periodsForDef(settings, def).slice().sort().join(',');
    return `${def.settingsKey}:${enabled}:${periods}`;
  }).join('|');

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapLoaded) return;

    const apply = () => {
      const activeMap = mapRef.current;
      if (!activeMap?.isStyleLoaded()) return;
      syncForecastWeatherLayers(activeMap, settings);
    };

    apply();
    map.on('style.load', apply);
    return () => {
      map.off('style.load', apply);
    };
  }, [mapRef, isMapLoaded, syncKey, settings]);
}
